import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, snapCenterToCursor } from '@dnd-kit/modifiers';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Columns3, GripVertical } from 'lucide-react';
import { useCallback, useMemo, useState, type HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/DropdownMenu';

type ColumnPickerOption<TId extends string> = {
    id: TId;
    label: string;
};

type DataTableColumnsMenuProps<TId extends string> = {
    options: ReadonlyArray<ColumnPickerOption<TId>>;
    columnOrder: readonly TId[];
    visibility: Record<TId, boolean>;
    onToggle: (id: TId, visible: boolean) => void;
    onReorder: (order: readonly TId[]) => void;
    onReset: () => void;
    triggerLabel?: string;
    menuLabel?: string;
    reorderHint?: string;
    resetLabel?: string;
};

type SortableColumnRowProps<TId extends string> = {
    id: TId;
    label: string;
    checked: boolean;
    onToggle: (id: TId, visible: boolean) => void;
};

function ColumnPickerDragHandle({
    label,
    dragHandleProps,
}: {
    label: string;
    dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
}) {
    return (
        <button
            type="button"
            className="nx-col-picker-handle"
            aria-label={`Reordenar coluna ${label}`}
            {...dragHandleProps}
        >
            <GripVertical size={14} strokeWidth={2} aria-hidden />
        </button>
    );
}

function ColumnPickerRowContent({
    label,
    checked,
    dragHandleProps,
}: {
    label: string;
    checked: boolean;
    dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
}) {
    return (
        <>
            <label className="nx-col-picker-check">
                <input type="checkbox" className="nx-checkbox" checked={checked} readOnly />
                <span>{label}</span>
            </label>

            <ColumnPickerDragHandle label={label} dragHandleProps={dragHandleProps} />
        </>
    );
}

function SortableColumnRow<TId extends string>({
    id,
    label,
    checked,
    onToggle,
}: SortableColumnRowProps<TId>) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <DropdownMenuItem
            className="nx-col-picker-item"
            onSelect={(event) => event.preventDefault()}
        >
            <div
                ref={setNodeRef}
                style={style}
                className={[
                    'nx-col-picker-row',
                    isDragging ? 'nx-col-picker-row--dragging' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <label className="nx-col-picker-check">
                    <input
                        type="checkbox"
                        className="nx-checkbox"
                        checked={checked}
                        onChange={(event) => onToggle(id, event.target.checked)}
                    />
                    <span>{label}</span>
                </label>

                <ColumnPickerDragHandle
                    label={label}
                    dragHandleProps={{
                        ...attributes,
                        ...listeners,
                        onClick: (event) => event.stopPropagation(),
                    }}
                />
            </div>
        </DropdownMenuItem>
    );
}

export function DataTableColumnsMenu<TId extends string>({
    options,
    columnOrder,
    visibility,
    onToggle,
    onReorder,
    onReset,
    triggerLabel = 'Colunas',
    menuLabel = 'Exibir colunas',
    reorderHint = 'Arraste para reordenar',
    resetLabel = 'Restaurar padrão',
}: DataTableColumnsMenuProps<TId>) {
    const [activeId, setActiveId] = useState<TId | null>(null);

    const orderedOptions = useMemo(() => {
        return [...options].sort((a, b) => {
            const aIndex = columnOrder.indexOf(a.id);
            const bIndex = columnOrder.indexOf(b.id);
            return (aIndex === -1 ? 0 : aIndex) - (bIndex === -1 ? 0 : bIndex);
        });
    }, [options, columnOrder]);

    const sortableIds = useMemo(() => orderedOptions.map((option) => option.id), [orderedOptions]);

    const activeOption = activeId
        ? orderedOptions.find((option) => option.id === activeId)
        : undefined;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 4 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as TId);
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            setActiveId(null);

            if (!over || active.id === over.id) {
                return;
            }

            const currentOrder = orderedOptions.map((option) => option.id);
            const oldIndex = currentOrder.indexOf(active.id as TId);
            const newIndex = currentOrder.indexOf(over.id as TId);

            if (oldIndex === -1 || newIndex === -1) {
                return;
            }

            onReorder(arrayMove(currentOrder, oldIndex, newIndex));
        },
        [onReorder, orderedOptions],
    );

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
    }, []);

    const preventCloseWhileDragging = useCallback(
        (event: Event) => {
            if (activeId) {
                event.preventDefault();
            }
        },
        [activeId],
    );

    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" className="btn-sm">
                    <Columns3 size={16} strokeWidth={2} />
                    {triggerLabel}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                sideOffset={8}
                collisionPadding={12}
                className="nx-col-picker-menu"
                onPointerDownOutside={preventCloseWhileDragging}
                onInteractOutside={preventCloseWhileDragging}
                onCloseAutoFocus={(event) => {
                    if (activeId) {
                        event.preventDefault();
                    }
                }}
            >
                <div className="nx-col-picker-header">
                    <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
                    <p className="nx-col-picker-hint">{reorderHint}</p>
                </div>

                <div className="nx-col-picker-list">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        measuring={{
                            droppable: { strategy: MeasuringStrategy.Always },
                        }}
                        modifiers={[restrictToVerticalAxis]}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                            {orderedOptions.map((option) => (
                                <SortableColumnRow
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    checked={visibility[option.id]}
                                    onToggle={onToggle}
                                />
                            ))}
                        </SortableContext>

                        {typeof document !== 'undefined'
                            ? createPortal(
                                  <DragOverlay
                                      modifiers={[snapCenterToCursor]}
                                      dropAnimation={{
                                          duration: 180,
                                          easing: 'cubic-bezier(0.2, 0, 0, 1)',
                                      }}
                                      zIndex={10000}
                                  >
                                      {activeOption ? (
                                          <div className="nx-col-picker-row nx-col-picker-row--overlay">
                                              <ColumnPickerRowContent
                                                  label={activeOption.label}
                                                  checked={visibility[activeOption.id]}
                                              />
                                          </div>
                                      ) : null}
                                  </DragOverlay>,
                                  document.body,
                              )
                            : null}
                    </DndContext>
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuItem onSelect={onReset}>{resetLabel}</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
